import React, { useEffect, useMemo, useState } from "react";
import api from "../../../shared/api/apiClient";
import "./MarketingContactPanel.css";
import { useTranslation } from "react-i18next";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
};

const normalizeBirthDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
};

const formatBirthDate = (value) => {
  const normalized = normalizeBirthDate(value);
  if (!normalized) return "—";

  const [year, month, day] = normalized.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

const todayInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const couponDiscount = (coupon, t) => {
  if (!coupon) return "—";
  const value = coupon.discount_value;
  if (value === null || value === undefined || value === "") return "—";
  if (coupon.discount_type === "percent") return `${value}%`;
  if (coupon.discount_type === "fixed") return `${value}`;
  if (coupon.discount_type === "gift") return t("marketing_gift_value", { value });
  return `${value}`;
};

function CouponRow({ coupon }) {
  const { t } = useTranslation();
  return (
    <div className="marketingContactCoupon">
      <div>
        <strong>{coupon.code}</strong>
        <span>{coupon.coupon_type}</span>
      </div>
      <div>
        <b>{couponDiscount(coupon, t)}</b>
        <small>{coupon.ends_at ? `${t("marketing_valid_until")} ${formatDate(coupon.ends_at)}` : t("marketing_no_expiration")}</small>
      </div>
    </div>
  );
}

export default function MarketingContactPanel({ contactId, canEdit = false }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [birthdayEditing, setBirthdayEditing] = useState(false);
  const [birthdayDraft, setBirthdayDraft] = useState("");
  const [birthdaySaving, setBirthdaySaving] = useState(false);
  const [birthdayError, setBirthdayError] = useState("");

  const activeCoupons = useMemo(() => data?.active_coupons || [], [data]);
  const usedCoupons = useMemo(() => data?.used_coupons || [], [data]);
  const maxBirthDate = useMemo(() => todayInputValue(), []);
  const telegramUsername = String(data?.telegram_username || "")
    .trim()
    .replace(/^@+/, "");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!contactId) return;
      setLoading(true);
      setError("");
      try {
        const { data: response } = await api.get(`/marketing/contacts/${contactId}/block`);
        if (!cancelled) {
          setData(response);
          setBirthdayDraft(normalizeBirthDate(response?.birth_date));
          setBirthdayEditing(false);
          setBirthdayError("");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(err?.response?.data?.detail || t("marketing_contact_load_failed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [contactId, t]);

  const openBirthdayEditor = () => {
    setBirthdayDraft(normalizeBirthDate(data?.birth_date));
    setBirthdayError("");
    setBirthdayEditing(true);
  };

  const closeBirthdayEditor = () => {
    if (birthdaySaving) return;
    setBirthdayDraft(normalizeBirthDate(data?.birth_date));
    setBirthdayError("");
    setBirthdayEditing(false);
  };

  const saveBirthday = async (nextValue = birthdayDraft) => {
    const normalizedValue = normalizeBirthDate(nextValue);

    if (normalizedValue && normalizedValue > maxBirthDate) {
      setBirthdayError(t("marketing_birthday_future_error"));
      return;
    }

    setBirthdaySaving(true);
    setBirthdayError("");

    try {
      const { data: response } = await api.put(
        `/marketing/contacts/${contactId}/birthday`,
        { birth_date: normalizedValue || null }
      );
      const savedBirthDate = normalizeBirthDate(response?.birth_date);
      setData((previous) => ({
        ...previous,
        birth_date: savedBirthDate || null,
      }));
      setBirthdayDraft(savedBirthDate);
      setBirthdayEditing(false);
    } catch (err) {
      console.error(err);
      setBirthdayError(
        err?.response?.data?.detail || t("marketing_birthday_save_failed")
      );
    } finally {
      setBirthdaySaving(false);
    }
  };

  if (loading) {
    return <div className="marketingContactLoading">{t("marketing_loading")}</div>;
  }

  if (error) {
    return <div className="alert alert-danger py-2 mb-0">{error}</div>;
  }

  if (!data) {
    return <div className="text-muted">{t("marketing_no_data")}</div>;
  }

  return (
    <div className="marketingContactPanel">
      <div className="marketingContactGrid">
        <div className="marketingContactMetric">
          <span>{t("marketing_telegram_username")}</span>
          <strong>{telegramUsername ? `@${telegramUsername}` : t("marketing_telegram_username_missing")}</strong>
        </div>
        <div className="marketingContactMetric">
          <span>{t("marketing_telegram_bot_connection")}</span>
          <strong className={data.telegram_connected ? "isPositive" : "isMuted"}>
            {data.telegram_connected ? t("connected") : t("not_connected")}
          </strong>
        </div>
        <div className="marketingContactMetric">
          <span>{t("email")}</span>
          <strong>{data.email || "—"}</strong>
        </div>
        <div className="marketingContactMetric marketingContactBirthdayMetric">
          <div className="marketingContactMetricHeader">
            <span>{t("date_of_birth")}</span>
            {canEdit && !birthdayEditing && (
              <button
                type="button"
                className="marketingContactBirthdayEdit"
                onClick={openBirthdayEditor}
              >
                {data.birth_date
                  ? t("marketing_edit_birthday")
                  : t("marketing_add_birthday")}
              </button>
            )}
          </div>

          {birthdayEditing ? (
            <div className="marketingContactBirthdayEditor">
              <input
                type="date"
                className="form-control form-control-sm"
                value={birthdayDraft}
                max={maxBirthDate}
                disabled={birthdaySaving}
                aria-label={t("date_of_birth")}
                onChange={(event) => {
                  setBirthdayDraft(event.target.value);
                  setBirthdayError("");
                }}
              />
              <div className="marketingContactBirthdayActions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={birthdaySaving}
                  onClick={() => saveBirthday()}
                >
                  {birthdaySaving ? t("saving") : t("save")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={birthdaySaving}
                  onClick={closeBirthdayEditor}
                >
                  {t("cancel")}
                </button>
                {data.birth_date && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={birthdaySaving}
                    onClick={() => saveBirthday("")}
                  >
                    {t("remove")}
                  </button>
                )}
              </div>
              {birthdayError && (
                <small className="marketingContactBirthdayError">
                  {birthdayError}
                </small>
              )}
            </div>
          ) : (
            <strong>{formatBirthDate(data.birth_date)}</strong>
          )}
        </div>
        <div className="marketingContactMetric">
          <span>{t("marketing_email_permission")}</span>
          <strong className={data.email_allowed ? "isPositive" : "isMuted"}>
            {data.email_allowed ? t("allowed") : t("forbidden")}
          </strong>
        </div>
        <div className="marketingContactMetric">
          <span>{t("marketing_telegram_permission")}</span>
          <strong className={data.telegram_allowed ? "isPositive" : "isMuted"}>
            {data.telegram_allowed ? t("allowed") : t("forbidden")}
          </strong>
        </div>
        <div className="marketingContactMetric">
          <span>{t("marketing_last_campaign")}</span>
          <strong>{data.last_campaign?.title || "—"}</strong>
        </div>
      </div>

      {telegramUsername && !data.telegram_connected && (
        <div className="marketingContactTelegramNotice">
          <strong>{t("marketing_telegram_username_not_connection_title")}</strong>
          <span>{t("marketing_telegram_username_not_connection_hint")}</span>
        </div>
      )}

      <div className="marketingContactSection">
        <div className="marketingContactSectionTitle">
          <strong>{t("marketing_active_campaigns")}</strong>
          <span>{activeCoupons.length}</span>
        </div>
        {activeCoupons.length > 0 ? (
          activeCoupons.map((coupon) => <CouponRow key={coupon.id} coupon={coupon} />)
        ) : (
          <p className="marketingContactEmpty">{t("marketing_no_active_coupons")}</p>
        )}
      </div>

      <div className="marketingContactSection">
        <div className="marketingContactSectionTitle">
          <strong>{t("marketing_used_coupons")}</strong>
          <span>{usedCoupons.length}</span>
        </div>
        {usedCoupons.length > 0 ? (
          usedCoupons.map((coupon) => <CouponRow key={coupon.id} coupon={coupon} />)
        ) : (
          <p className="marketingContactEmpty">{t("marketing_no_used_coupons")}</p>
        )}
      </div>
    </div>
  );
}
